import React, { useCallback, useEffect, useState } from 'react';
import Route404 from 'App/Router/Route404';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Alert, GradientBackground, Loader } from '@unmind/design-system';
import { useTheme } from 'styled-components';
import { ErrorButton } from 'Assistant/Chatbot/components/Error/styles';
import { useAuthHeader } from 'Assistant/Chatbot/hooks/useAuthHeader';
import { generateVoiceTokenVoiceTokenPost as generateVoiceAccessToken } from 'Assistant/__generated__/client';
import {
  ControlPanel,
  VoiceRoomContainer,
} from 'Assistant/Chatbot/components/Voice';
import { tracking } from 'App/Tracking';
import { useShowVoiceDisclaimer } from 'Assistant/Chatbot/hooks';
import RoutePath from 'App/RoutePath';
import { HelpButton } from 'Assistant/Chatbot/components/Voice/HelpButton';
import useUserEntitlements from 'hooks/useUserEntitlements';
import { logException } from 'App/logging';
import {
  LoadingContainer,
  MicrophoneSettingsButton,
  MicrophoneSettingsContainer,
  MicrophoneSettingsText,
  PageContainer,
} from './styles';

type MicrophonePermissionState = PermissionState | 'error';

const redirectToSignIn = () => {
  const SIGNIN_PATH = '/?redirect=';
  const signinUrl = `${window.location.origin}${SIGNIN_PATH}${window.location.pathname}`;
  window.location.assign(signinUrl);
};

export const VoicePage = () => {
  const { entitlements, loading: entitlementsLoading } = useUserEntitlements();
  const isNovaVoiceEnabled = entitlements?.novaVoice;
  const { t: translate } = useTranslation('chatbot');
  const history = useHistory();
  const theme = useTheme();
  const [microphonePermissionState, setMicrophonePermissionState] =
    useState<MicrophonePermissionState>();
  const [voiceAccessToken, setVoiceAccessToken] = useState<string>();
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [callError, setCallError] = useState(false);
  const accessTokenHeader = useAuthHeader();
  const { showVoiceDisclaimer } = useShowVoiceDisclaimer();

  useEffect(() => {
    if (showVoiceDisclaimer || !isNovaVoiceEnabled) {
      return;
    }

    const fetchMicrophonePermission = async () => {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        });
        setMicrophonePermissionState(permissionStatus.state);
        permissionStatus.onchange = () => {
          setMicrophonePermissionState(permissionStatus.state);
        };
      } catch (error) {
        logException(error, {
          tags: {
            product: 'nova',
            feature: 'nova-voice',
            action: 'microphone-permission-query',
          },
          extra: {
            browser: navigator.userAgent,
            showVoiceDisclaimer,
          },
        });
        setMicrophonePermissionState('error');
      }
    };

    void fetchMicrophonePermission();
  }, [showVoiceDisclaimer, isNovaVoiceEnabled]);

  const onRequestMicrophonePermissions = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      logException(error, {
        tags: {
          product: 'nova',
          feature: 'nova-voice',
          action: 'microphone-permission-request',
        },
        extra: {
          browser: navigator.userAgent,
          currentState: microphonePermissionState,
        },
      });
      setMicrophonePermissionState('error');
    }
  };

  const fetchVoiceAccessToken = useCallback(async () => {
    try {
      setShowErrorMessage(false);
      const response = await generateVoiceAccessToken(
        { previous_call_errored: callError },
        {
          headers: {
            'client-platform': 'web',
            ...accessTokenHeader,
          },
        },
      );

      if (response.status === 401) {
        redirectToSignIn();
      }

      if (response.status !== 200) {
        logException(
          new Error(`Voice token fetch failed with status ${response.status}`),
          {
            tags: {
              product: 'nova',
              feature: 'nova-voice',
              action: 'fetch-voice-token',
            },
            extra: {
              statusCode: response.status,
              callError,
              hasAccessTokenHeader: !!accessTokenHeader,
            },
          },
        );
        setShowErrorMessage(true);

        return;
      }

      setVoiceAccessToken(response.data);
    } catch (error) {
      logException(error, {
        tags: {
          product: 'nova',
          feature: 'nova-voice',
          action: 'fetch-voice-token',
        },
        extra: {
          callError,
          hasAccessTokenHeader: !!accessTokenHeader,
          microphoneState: microphonePermissionState,
        },
      });
      setShowErrorMessage(true);
    }
  }, [accessTokenHeader, callError, microphonePermissionState]);

  useEffect(() => {
    if (
      isNovaVoiceEnabled &&
      microphonePermissionState === 'granted' &&
      !voiceAccessToken &&
      !showErrorMessage
    ) {
      void fetchVoiceAccessToken();
    }
  }, [
    isNovaVoiceEnabled,
    microphonePermissionState,
    voiceAccessToken,
    showErrorMessage,
    fetchVoiceAccessToken,
  ]);

  const handleRetry = () => {
    setVoiceAccessToken(undefined);
    setShowErrorMessage(false);
  };

  const handleError = () => {
    setVoiceAccessToken(undefined);
    setCallError(true);
    setShowErrorMessage(true);
  };

  const handleLeaveChat = (track?: boolean) => {
    if (track) {
      tracking.track('nova-voice-leave', { reason: 'user_initiated' });
    }

    setVoiceAccessToken(undefined);
    history.push(RoutePath.Chatbot, { requestVoiceFeedback: true });
  };

  const handleHelpRequired = () => {
    setVoiceAccessToken(undefined);
  };

  if (entitlementsLoading) {
    return (
      <PageContainer>
        <GradientBackground />
        <LoadingContainer
          aria-label={translate('conversation.loading.a11y_label')}
        >
          <Loader color={theme.colors.text.primary} />
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (!isNovaVoiceEnabled) {
    return <Route404 />;
  }

  if (microphonePermissionState !== 'granted' && !showVoiceDisclaimer) {
    return (
      <PageContainer>
        <GradientBackground />
        <HelpButton handleHelpRequired={handleHelpRequired} />
        <MicrophoneSettingsContainer>
          {microphonePermissionState === 'prompt' ? (
            <MicrophoneSettingsText>
              <Trans
                t={translate}
                i18nKey={'voice.microphone_permissions.prompt'}
                components={{
                  settings_button: (
                    <MicrophoneSettingsButton
                      onClick={onRequestMicrophonePermissions}
                    />
                  ),
                }}
              />
            </MicrophoneSettingsText>
          ) : (
            <MicrophoneSettingsText>
              {translate('voice.microphone_permissions.denied')}
            </MicrophoneSettingsText>
          )}
          <ControlPanel
            handleLeaveChat={() => handleLeaveChat(true)}
            microphoneButtonDisabled
          />
        </MicrophoneSettingsContainer>
      </PageContainer>
    );
  }

  if (showErrorMessage) {
    return (
      <PageContainer>
        <GradientBackground />
        <HelpButton handleHelpRequired={handleHelpRequired} />
        <LoadingContainer>
          <Alert
            variant="error"
            size="medium"
            title={translate('voice.access_token.header')}
            orientation="vertical"
          >
            <ErrorButton
              label={translate('voice.access_token.retry_button.label')}
              aria-label={translate('voice.access_token.retry_button.label')}
              variant="secondary"
              size="medium"
              $orientation="vertical"
              onClick={handleRetry}
            />
          </Alert>
        </LoadingContainer>
        <ControlPanel
          handleLeaveChat={() => handleLeaveChat(true)}
          microphoneButtonDisabled
        />
      </PageContainer>
    );
  }

  if (!voiceAccessToken || showVoiceDisclaimer) {
    if (showVoiceDisclaimer) {
      history.replace(RoutePath.VoiceDisclaimer);
    }

    return (
      <PageContainer>
        <GradientBackground />
        <HelpButton handleHelpRequired={handleHelpRequired} />
        <LoadingContainer
          aria-label={translate('conversation.loading.a11y_label')}
        >
          <Loader color={theme.colors.text.primary} />
        </LoadingContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <GradientBackground />
      <VoiceRoomContainer
        voiceAccessToken={voiceAccessToken}
        onLeaveChat={handleLeaveChat}
        onError={handleError}
        handleHelpRequired={handleHelpRequired}
      />
    </PageContainer>
  );
};
